import React, { useRef, useState } from "react";
import * as chatApi from "./chatApi";

// =====================================================
// BACKUP DIALOG
// Export the signed-in user's chat history to a downloadable JSON file, and
// restore from a previously exported file. The server does the heavy lifting
// (decrypt on export, dedupe + re-encrypt on restore); this dialog handles the
// browser-side file download / upload and shows the outcome.
// =====================================================

// Flatten the exported { conversations: { partnerId: [msgs] } } shape into a
// single messages array the restore endpoint accepts.
const flattenBackup = (backup) => {
  if (Array.isArray(backup?.messages)) return backup.messages;
  const convos = backup?.conversations || {};
  return Object.values(convos).flat();
};

function BackupDialog({ userId, onClose }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleExport = async () => {
    setBusy(true);
    setError("");
    setResult("");
    try {
      const res = await chatApi.exportBackup(userId);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chat-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setResult(
        `Exported ${res.data.messageCount} messages from ${res.data.conversationCount} conversations.`
      );
    } catch (err) {
      setError(err.response?.data?.error || "Export failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    setResult("");
    try {
      const text = await file.text();
      const backup = JSON.parse(text);
      const messages = flattenBackup(backup);
      if (!messages.length) {
        setError("This file has no messages to restore.");
        return;
      }
      const res = await chatApi.restoreBackup(userId, messages);
      setResult(
        `Restored ${res.data.restored} messages (${res.data.skipped} already present).`
      );
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Restore failed — is this a valid backup file?"
      );
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="chat-confirm-overlay" onClick={onClose}>
      <div className="backup-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="forward-head">
          <h3>💾 Chat Backup</h3>
          <button className="forward-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <p className="backup-desc">
          Export your entire chat history to a file you control, or restore it
          from a backup. Restoring never creates duplicates.
        </p>

        <div className="backup-actions">
          <button
            className="chat-confirm-yes backup-btn"
            onClick={handleExport}
            disabled={busy}
          >
            {busy ? "Working…" : "⬇ Export backup"}
          </button>

          <button
            className="chat-confirm-cancel backup-btn"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            ⬆ Restore from file
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            style={{ display: "none" }}
            onChange={handleRestore}
          />
        </div>

        {result && <div className="backup-result ok">{result}</div>}
        {error && <div className="backup-result err">{error}</div>}
      </div>
    </div>
  );
}

export default BackupDialog;
